import { Banner, type SelectOption } from '@components';
import { Box, Button, Divider, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Formik, type FormikHelpers, getIn, useFormikContext } from 'formik';
import { Add, Trash } from 'iconsax-react';
import {
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as Yup from 'yup';

import { SlabContext } from '@core/context/SlabContext';
import { NewBaseContactSchema } from '@core/formik/contact';
import type { ContactData } from '@core/hooks/useContactCreation';
import { useDebounceValue } from '@core/hooks/useDebounceValue';
import { Address } from '@core/types/api/Address/Address';
import { Company } from '@core/types/api/Company/Company';
import { Contact } from '@core/types/api/Contact/Contact';
import Enums from '@core/types/api/Enums';
import type { Market } from '@core/types/api/Market/Market';
import { hasStringValue, hasValue } from '@core/utils/equality';
import { requiredLabel } from '@core/utils/strings';
import { YupAddress } from '@core/utils/yupHelpers';
import { MultiLookupInput } from '@web/components';
import { AddressField } from '@web/components/Address/AddressField';
import { ContactField } from '@web/components/Contact/ContactField';
import { Input } from '@web/components/Input/Input';
import { ReferenceValueMatcher } from '@web/components/LookupInput/utils';
import { SegmentedControl } from '@web/components/SegmentedControl/SegmentedControl';
import { SlabButton } from '@web/components/SlabButton/SlabButton';
import { SlabCard } from '@web/components/SlabCard/SlabCard';
import { SlabCardBody } from '@web/components/SlabCard/SlabCardBody';
import { SlabCardHeader } from '@web/components/SlabCard/SlabCardHeader';
import { LOADING_INDICATOR_DELAY_MS } from '@web/constants/app';
import { useSlabMutation } from '@web/hooks/useSlabMutation';
import { ServerErrorMessage, useSlabQuery } from '@web/hooks/useSlabQuery';
import { useToast } from '@web/providers/ToastProvider/useToast';
import { lookups } from '@web/utils/DomainHelpers';

type CompanyReference = {
  id: string;
  option: { value: string; label: string };
};

type Props = {
  children: ReactNode;
  onCompanyCreated: (company: CompanyReference, contact: ContactData | null) => void;
};

type ContactValues = {
  firstName: string;
  lastName: string;
  jobTitle: string;
  emailAddress: string;
  phoneNumber: string;
};

type FormValues = {
  name: string;
  markets: { id: string; option: { value: string; label: string } }[];
  contact: ContactValues | null;
  address: Address | null;
};

const CreateButton: FC<{ isSubmitting: boolean; onClick: () => void }> = ({
  isSubmitting,
  onClick,
}) => {
  const isSlow = useDebounceValue(isSubmitting, LOADING_INDICATOR_DELAY_MS) && isSubmitting;
  return (
    <SlabButton
      type='button'
      variant='slab-primary'
      loading={isSlow}
      onClick={onClick}
      disabled={isSubmitting}
    >
      Create
    </SlabButton>
  );
};

export const InlineCompanyCreate: FC<Props> = ({ children, onCompanyCreated }) => {
  const theme = useTheme();
  const ctx = useContext(SlabContext);
  const toast = useToast();

  // Access the outer quote Formik so we can touch / surface the company field error
  // when the inner create form is shown instead of the ApiLookupInput.
  const outerFormik = useFormikContext<any>();

  const [mode, setMode] = useState<'select' | 'create'>('select');
  const [createFormKey, setCreateFormKey] = useState(0);
  const attemptRef = useRef(0);
  const wasOuterDirtyRef = useRef(outerFormik.dirty);
  const previousCompanyRef = useRef<unknown>(null);

  // Touch the outer company field whenever a submit is attempted while in create mode,
  // so the validation error becomes visible.
  useEffect(() => {
    if (mode === 'create' && outerFormik.submitCount > 0) {
      outerFormik.setFieldTouched('company', true, false);
    }
  }, [outerFormik.submitCount]);

  // When the outer form is reset, also reset the inline create-company UI/form.
  useEffect(() => {
    const wasDirty = wasOuterDirtyRef.current;
    const wasReset = wasDirty && !outerFormik.dirty;

    if (wasReset) {
      attemptRef.current += 1;
      previousCompanyRef.current = null;
      setMode('select');
      setCreateFormKey((prev) => prev + 1);
      outerFormik.setFieldTouched('company', false, false);
      outerFormik.setFieldError('company', undefined);
    }

    wasOuterDirtyRef.current = outerFormik.dirty;
  }, [outerFormik.dirty, outerFormik]);

  const areMarketsRequired = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagRequiresCompanyMarkets,
  ]);
  const requiresContactInfo = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagRequiresContactInfo,
  ]);
  const requiresContactEmail = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagRequiresContactEmail,
  ]);

  const validationSchema = useMemo(() => {
    const markets = Yup.array().of(
      Yup.object({
        id: Yup.string().required(),
        option: Yup.object({
          value: Yup.string().required(),
          label: Yup.string().required(),
        }).required(),
      }).required(),
    );
    return Yup.object({
      name: Yup.string().required('Company name is required'),
      markets: areMarketsRequired ? markets.min(1, 'At least one market is required') : markets,
      contact: NewBaseContactSchema({
        fullContactInfo: requiresContactInfo,
        requiresEmail: requiresContactEmail,
      })
        .nullable()
        .default(null),
      address: YupAddress('Address').nullable().notRequired(),
    });
  }, [areMarketsRequired, requiresContactInfo, requiresContactEmail]);

  const { data: marketList } = useSlabQuery('GET markets', {});
  const marketOpts: SelectOption<string>[] =
    marketList === undefined ? [] : lookups({ ...marketList, label: (m: Market) => m.name ?? '' });

  const createContact = useSlabMutation('POST contact', {
    onError: (error) => {
      toast.error({ message: ServerErrorMessage(error) });
    },
  });

  const createCompany = useSlabMutation('POST company', {
    onError: (error) => {
      toast.error({ message: ServerErrorMessage(error) });
    },
  });

  const handleCompanyCreated = (data: Company, values: FormValues, attempt: number): void => {
    const isStale = (): boolean => attempt !== attemptRef.current;
    if (isStale()) {
      return;
    }

    const company = { id: data.id, option: { value: data.id, label: data.name } };
    const finish = (contact: ContactData | null): void => {
      if (isStale()) {
        return;
      }
      onCompanyCreated(company, contact);
      setMode('select');
    };

    const contactValues = values.contact;
    if (!hasValue(contactValues)) {
      finish(null);
      return;
    }

    const { firstName, lastName, jobTitle, emailAddress, phoneNumber } = contactValues;
    const wireContact = new Contact({
      firstName,
      lastName,
      jobTitle: hasStringValue(jobTitle) ? jobTitle : null,
      emailAddress: hasStringValue(emailAddress) ? emailAddress : null,
      phoneNumber: hasStringValue(phoneNumber) ? phoneNumber : null,
      company: { id: data.id },
    });
    createContact.mutate(
      { args: { body: wireContact } },
      {
        onSuccess: (contact) =>
          finish({
            id: contact.id,
            option: { value: contact.id, label: contact.fullName() },
            isNewContact: false,
          }),
        onError: () => finish(null),
      },
    );
  };

  const handleCancel = useCallback(() => {
    attemptRef.current += 1;
    setMode('select');
    if (previousCompanyRef.current !== null) {
      outerFormik.setFieldValue('company', previousCompanyRef.current);
      previousCompanyRef.current = null;
    }
  }, [outerFormik]);

  const handleSubmit = (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>): void => {
    attemptRef.current += 1;
    const attempt = attemptRef.current;
    const wireCompany = new Company({
      name: values.name,
      category: Enums.CompanyCategory.Contractor,
      marketSummaries: values.markets.map((m) => ({ id: m.id })),
      address: values.address ?? undefined,
    });
    createCompany.mutate(
      { args: { body: wireCompany } },
      {
        onSuccess: (data) => handleCompanyCreated(data, values, attempt),
        onError: () => setSubmitting(false),
      },
    );
  };

  const initialValues: FormValues = { name: '', markets: [], contact: null, address: null };

  const companyCreateForm = (
    <Formik<FormValues>
      key={createFormKey}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formikBag) => {
        const hasCompanyName = formikBag.values.name.trim() !== '';
        return (
          <SlabCard>
            <SlabCardHeader>
              <Typography variant='subtitle2'>New Company</Typography>
            </SlabCardHeader>
            <SlabCardBody>
              <Box sx={{ mb: 1 }}>
                <Banner severity='warning'>
                  Please ensure that you click Create to save the New Company. Once saved, you can
                  proceed with the next steps.
                </Banner>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Input
                    name='name'
                    label={requiredLabel('Company Name', true)}
                    onInputChange={(value) => {
                      if (value.trim() !== '') {
                        outerFormik.setFieldTouched('company', false, false);
                        outerFormik.setFieldError('company', undefined);
                        return;
                      }

                      if (outerFormik.submitCount > 0) {
                        outerFormik.setFieldTouched('company', true, false);
                      }
                    }}
                  />
                </Box>
                {marketOpts.length > 0 && (
                  <Box sx={{ flex: 1 }}>
                    <MultiLookupInput
                      name='markets'
                      label={requiredLabel('Markets', areMarketsRequired)}
                      withCheckbox
                      tip='Select specific markets otherwise the company will be associated with all markets.'
                      options={marketOpts}
                      onMatchChange={(matches): void => {
                        formikBag.setFieldValue(
                          'markets',
                          matches.map(({ value, label }) => ({
                            id: value,
                            option: { value, label },
                          })),
                        );
                      }}
                      formValueMatcher={ReferenceValueMatcher}
                    />
                  </Box>
                )}
              </Box>

              {hasCompanyName && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant='body3' sx={{ color: theme.palette.SlabGray[600], mb: 1 }}>
                    Add contact and address info (optional). You can also do this later.
                  </Typography>
                  {hasValue(formikBag.values.contact) ? (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1 }}>
                        <ContactField
                          name='contact'
                          fieldPrefix=''
                          requiresContactInfo={requiresContactInfo}
                          requiresContactEmail={requiresContactEmail}
                        />
                      </Box>
                      <IconButton
                        aria-label='Remove contact'
                        sx={{ mt: 1 }}
                        onClick={() => formikBag.setFieldValue('contact', null)}
                      >
                        <Trash variant='TwoTone' color={theme.palette.SlabIndigo['700']} />
                      </IconButton>
                    </Box>
                  ) : (
                    <Button
                      type='button'
                      variant='slab-action-plain'
                      startIcon={<Add />}
                      sx={{ alignSelf: 'flex-start' }}
                      onClick={() =>
                        formikBag.setFieldValue('contact', {
                          firstName: '',
                          lastName: '',
                          jobTitle: '',
                          emailAddress: '',
                          phoneNumber: '',
                        })
                      }
                    >
                      Add Contact
                    </Button>
                  )}
                  <Divider sx={{ mt: 0, mb: 3 }} />
                  {hasValue(formikBag.values.address) ? (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1 }}>
                        <AddressField name='address' />
                      </Box>
                      <IconButton
                        aria-label='Remove address'
                        sx={{ mt: 1 }}
                        onClick={() => formikBag.setFieldValue('address', null)}
                      >
                        <Trash variant='TwoTone' color={theme.palette.SlabIndigo['700']} />
                      </IconButton>
                    </Box>
                  ) : (
                    <Button
                      type='button'
                      variant='slab-action-plain'
                      startIcon={<Add />}
                      sx={{ alignSelf: 'flex-start' }}
                      onClick={() => formikBag.setFieldValue('address', Address.zero())}
                    >
                      Add Address
                    </Button>
                  )}
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <SlabButton type='button' variant='slab-secondary' onClick={handleCancel}>
                  Cancel
                </SlabButton>
                <CreateButton
                  isSubmitting={formikBag.isSubmitting}
                  onClick={formikBag.submitForm}
                />
              </Box>
            </SlabCardBody>
          </SlabCard>
        );
      }}
    </Formik>
  );

  const outerCompanyError =
    mode === 'create' && getIn(outerFormik.touched, 'company')
      ? (getIn(outerFormik.errors, 'company') as string | undefined)
      : undefined;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SegmentedControl
        value={mode}
        size='large'
        sx={{ width: '38rem', maxWidth: '100%' }}
        items={[
          { id: 'select', label: 'Select existing company' },
          { id: 'create', label: 'Create new company' },
        ]}
        onChange={(val) => {
          const newMode = val as 'select' | 'create';
          if (newMode === 'select') {
            attemptRef.current += 1;
          }
          setMode(newMode);
          if (newMode === 'create') {
            // Touch the company field so errors surface when the user clicks Save
            // without completing the form.
            outerFormik.setFieldTouched('company', true, false);
            // Set a sentinel so the outer form becomes dirty, enabling the Reset button.
            previousCompanyRef.current = outerFormik.values.company;
            outerFormik.setFieldValue('company', { id: '', option: null });
          } else if (previousCompanyRef.current !== null) {
            // Restore the previous company value when switching back to select mode.
            outerFormik.setFieldValue('company', previousCompanyRef.current);
            previousCompanyRef.current = null;
          }
        }}
      />

      {mode === 'select' ? (
        children
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {outerCompanyError !== undefined && typeof outerCompanyError === 'string' && (
            <Typography
              variant='body2'
              sx={{ color: theme.palette.error.main, fontSize: 12, mt: -0.5 }}
            >
              {outerCompanyError}
            </Typography>
          )}
          {companyCreateForm}
        </Box>
      )}
    </Box>
  );
};
